/**
 * PDF viewer block: card with a button that opens a <dialog> reader rendered
 * by PDF.js. The library is loaded on demand (dynamic import → own chunk),
 * so regular pages do not carry it.
 */
const ZOOM_STEPS = [0.7, 0.85, 1, 1.15, 1.3];

const loadPdfJs = async () => {
    const [pdfjs, worker] = await Promise.all([
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    return pdfjs;
};

const initViewer = (root) => {
    const url = root.dataset.url;
    const openBtn = root.querySelector('[data-pdf-viewer-open]');
    const dialog = root.querySelector('[data-pdf-viewer-dialog]');

    if (!url || !openBtn || !dialog) {
        return;
    }

    // No <dialog> support: the "Neuer Tab" link does the job.
    if (typeof dialog.showModal !== 'function') {
        openBtn.addEventListener('click', () => window.open(url, '_blank', 'noopener'));
        return;
    }

    const scroller = root.querySelector('[data-pdf-viewer-pages]');
    const col = root.querySelector('[data-pdf-viewer-col]');
    const countEl = root.querySelector('[data-pdf-viewer-count]');
    const rail = root.querySelector('[data-pdf-viewer-rail]');
    const zoomEl = root.querySelector('[data-pdf-viewer-zval]');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let pdfDoc = null;
    let observer = null;
    let loading = null;
    let zoom = 1;
    const wrappers = [];
    const rendered = [];
    const thumbs = [];

    const renderPage = (index) => {
        if (rendered[index]) {
            return;
        }
        rendered[index] = true;
        pdfDoc.getPage(index + 1).then((page) => {
            const wrap = wrappers[index];
            const width = wrap.getBoundingClientRect().width;
            const base = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({ scale: (width / base.width) * dpr });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise.then(() => {
                wrap.innerHTML = '';
                wrap.appendChild(canvas);
            });
        });
    };

    const resetPages = () => {
        wrappers.forEach((wrap, index) => {
            rendered[index] = false;
            wrap.innerHTML = `<div class="pdf-viewer__ghost">Seite ${index + 1}</div>`;
            observer.unobserve(wrap);
            observer.observe(wrap);
        });
    };

    const currentPage = () => {
        const mid = scroller.scrollTop + scroller.clientHeight * 0.4;
        let current = 1;
        wrappers.forEach((wrap, index) => {
            if (wrap.offsetTop <= mid) {
                current = index + 1;
            }
        });
        return current;
    };

    const updateBar = () => {
        if (!pdfDoc) {
            return;
        }
        const current = currentPage();
        countEl.textContent = `Seite ${current} / ${pdfDoc.numPages}`;
        thumbs.forEach((thumb, index) => thumb.classList.toggle('is-active', index + 1 === current));
    };

    const goTo = (pageNo) => {
        const wrap = wrappers[pageNo - 1];
        if (wrap) {
            scroller.scrollTo({ top: Math.max(wrap.offsetTop - 14, 0) });
        }
    };

    const load = async () => {
        const pdfjs = await loadPdfJs();
        pdfDoc = await pdfjs.getDocument({ url }).promise;
        col.innerHTML = '';

        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        renderPage(Number(entry.target.dataset.page) - 1);
                    }
                });
            },
            { root: scroller, rootMargin: '900px 0px' }
        );

        for (let p = 1; p <= pdfDoc.numPages; p++) {
            const wrap = document.createElement('div');
            wrap.className = 'pdf-viewer__pwrap';
            wrap.dataset.page = String(p);
            wrap.innerHTML = `<div class="pdf-viewer__ghost">Seite ${p}</div>`;
            col.appendChild(wrap);
            wrappers.push(wrap);
            observer.observe(wrap);
        }

        let chain = Promise.resolve();
        for (let p = 1; p <= pdfDoc.numPages; p++) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'pdf-viewer__pthumb';
            button.setAttribute('aria-label', `Zu Seite ${p}`);
            button.innerHTML = `<span>${p}</span>`;
            button.addEventListener('click', () => goTo(p));
            rail.appendChild(button);
            thumbs.push(button);

            chain = chain.then(() =>
                pdfDoc.getPage(p).then((page) => {
                    const base = page.getViewport({ scale: 1 });
                    const viewport = page.getViewport({ scale: (110 / base.width) * 1.6 });
                    const canvas = document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    return page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise.then(() => {
                        button.insertBefore(canvas, button.firstChild);
                    });
                })
            );
        }

        scroller.addEventListener('scroll', updateBar, { passive: true });
        updateBar();
    };

    const open = () => {
        dialog.showModal();
        requestAnimationFrame(() => dialog.classList.add('is-in'));
        if (!loading) {
            loading = load().catch(() => {
                col.innerHTML = `<div class="pdf-viewer__status">Das PDF konnte nicht geladen werden. <a href="${url}" target="_blank" rel="noopener">In neuem Tab öffnen</a></div>`;
            });
        }
    };

    const stepZoom = (direction) => {
        const index = ZOOM_STEPS.indexOf(zoom);
        zoom = ZOOM_STEPS[Math.min(Math.max(index + direction, 0), ZOOM_STEPS.length - 1)];
        scroller.style.setProperty('--z', zoom);
        zoomEl.innerHTML = `${Math.round(zoom * 100)}&nbsp;%`;
        if (pdfDoc) {
            resetPages();
        }
    };

    openBtn.addEventListener('click', open);
    dialog.addEventListener('close', () => dialog.classList.remove('is-in'));
    root.querySelector('[data-pdf-viewer-close]')?.addEventListener('click', () => dialog.close());
    root.querySelectorAll('[data-pdf-viewer-zoom]').forEach((button) => {
        button.addEventListener('click', () => stepZoom(Number(button.dataset.pdfViewerZoom)));
    });
};

const init = () => document.querySelectorAll('[data-pdf-viewer]').forEach(initViewer);

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
